/**
 * Heuristics for auto-detecting a likely watermark column and primary key
 * columns from a source table's introspected schema metadata (SchemaColumn[]
 * — names/types only, never sample values, per the LLM-safety invariant).
 *
 * Used by SourceTablesPanel's "Auto-Detect" action on the driver row.
 */

import type { SchemaColumn } from "./types";

/** Column name substrings that strongly suggest an incremental/SCD watermark. */
const WATERMARK_NAME_HINTS = [
  "updated_at",
  "updated_on",
  "modified_at",
  "modified_on",
  "modified_date",
  "last_modified",
  "last_updated",
  "change_date",
  "changed_at",
  "timestamp",
  "ts",
  "created_at",
  "created_on",
  "date_modified",
  "date_updated",
];

/** Column type substrings that indicate a date/time-capable column. */
const WATERMARK_TYPE_HINTS = ["timestamp", "datetime", "date", "time"];

function matchesAny(haystack: string, needles: string[]): boolean {
  const lower = haystack.toLowerCase();
  return needles.some((needle) => lower.includes(needle));
}

/**
 * Return columns likely to be good watermark candidates, best match first.
 * A column scores higher when both its name AND type hint at a timestamp;
 * name-only or type-only matches are included but ranked lower. Returns an
 * empty array (not a throw) when nothing looks like a plausible candidate —
 * callers should fall back to offering the full column list.
 */
export function detectWatermarkCandidates(
  columns: SchemaColumn[],
): SchemaColumn[] {
  const scored = columns
    .map((col) => {
      const nameMatch = matchesAny(col.name, WATERMARK_NAME_HINTS);
      const typeMatch = matchesAny(col.type, WATERMARK_TYPE_HINTS);
      const score = (nameMatch ? 2 : 0) + (typeMatch ? 1 : 0);
      return { col, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.map((entry) => entry.col);
}

/** Return the names of columns flagged as primary keys by introspection. */
export function detectPrimaryKeyColumns(columns: SchemaColumn[]): string[] {
  return columns.filter((col) => col.isPk).map((col) => col.name);
}
